import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../config';
import { 
  Plus, 
  Trash2, 
  UploadCloud, 
  ShieldCheck, 
  Car, 
  User, 
  MapPin, 
  Phone,
  Send,
  Zap,
  CheckCircle2,
  ChevronRight,
  ChevronLeft
} from 'lucide-react';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import Swal from 'sweetalert2';
import './Contact.css';

const ContactInput = ({ label, type = "text", placeholder, value, onChange, name, required = false }) => {
  const [isFocused, setIsFocused] = useState(false);
  return (
    <div className="contact-field relative mt-8 mb-4">
      <label className="contact-field-label" style={{ color: 'var(--primary)', transform: 'translateY(-22px) scale(0.8)' }}>
        {label}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className="contact-field-input py-2"
        placeholder={placeholder}
      />
      <div className="contact-field-glow" style={{ width: isFocused ? '100%' : '0%', opacity: isFocused ? 1 : 0 }}></div>
    </div>
  );
};

const ContactSelect = ({ label, name, value, onChange, options, disabled = false, defaultText }) => {
  const [isFocused, setIsFocused] = useState(false);
  return (
    <div className="contact-field relative mt-8 mb-4">
      <label className="contact-field-label" style={{ color: 'var(--primary)', transform: 'translateY(-22px) scale(0.8)' }}>
        {label}
      </label>
      <div style={{position: 'relative'}}>
         <select
           name={name}
           value={value}
           onChange={onChange}
           disabled={disabled}
           onFocus={() => setIsFocused(true)}
           onBlur={() => setIsFocused(false)}
           className="contact-field-input py-2"
           style={{ appearance: 'none', cursor: 'pointer', paddingRight: '20px', width: '100%', backgroundColor: 'transparent', colorScheme: 'dark' }}
         >
           {defaultText && <option value="" style={{ backgroundColor: '#0b0c0f', color: '#ffffff' }}>{defaultText}</option>}
           {options.map(opt => <option key={opt.value || opt} value={opt.value || opt} style={{ backgroundColor: '#0b0c0f', color: '#ffffff' }}>{opt.label || opt}</option>)}
         </select>
         <div style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#00d1ff', opacity: 0.8 }}>↓</div>
      </div>
      <div className="contact-field-glow" style={{ width: isFocused ? '100%' : '0%', opacity: isFocused ? 1 : 0 }}></div>
    </div>
  );
};

const ALL_PARTS = [
  { key: 'hood', label: 'Kaput' },
  { key: 'roof', label: 'Tavan' },
  { key: 'trunk', label: 'Bagaj' },
  { key: 'front_bumper', label: 'Ön Tampon' },
  { key: 'rear_bumper', label: 'Arka Tampon' },
  { key: 'front_left_fender', label: 'Sol Ön Çamurluk' },
  { key: 'front_right_fender', label: 'Sağ Ön Çamurluk' },
  { key: 'rear_left_fender', label: 'Sol Arka Çamurluk' },
  { key: 'rear_right_fender', label: 'Sağ Arka Çamurluk' },
  { key: 'front_left_door', label: 'Sol Ön Kapı' },
  { key: 'front_right_door', label: 'Sağ Ön Kapı' },
  { key: 'rear_left_door', label: 'Sol Arka Kapı' },
  { key: 'rear_right_door', label: 'Sağ Arka Kapı' },
];

const COLOR_MAP = { original: '#1e293b', local_painted: '#f97316', painted: '#3b82f6', changed: '#ef4444' };

const SellCar = () => {
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    phone: '',
    email: '',
    brand: '',
    model: '',
    year: '',
    mileage: '',
    fuel_type: 'Benzin',
    transmission: 'Otomatik',
    expected_price: '',
    notes: '',
  });

  const [expertise, setExpertise] = useState(
    ALL_PARTS.reduce((acc, part) => ({ ...acc, [part.key]: 'original' }), {})
  );

  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [kvkkApproved, setKvkkApproved] = useState(false);
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API_URL}/brands`)
      .then(res => res.json())
      .then(data => setBrands(data))
      .catch(err => console.error('Markalar yüklenemedi:', err));
  }, []);

  useEffect(() => {
    if (formData.brand) {
      fetch(`${API_URL}/models?brand=${formData.brand}`)
        .then(res => res.json())
        .then(data => setModels(data.Results || []))
        .catch(err => console.error('Modeller yüklenemedi:', err));
    }
  }, [formData.brand]);

  const handleInputChange = (e) => {
    let { name, value } = e.target;

    if (name === 'phone') {
      value = value.replace(/\D/g, '');
      if (value.length > 0 && value[0] !== '0') {
        value = '0' + value;
      }
      if (value.length > 11) value = value.slice(0, 11);
      
      if (value.length > 3 && value.length <= 6) {
        value = value.slice(0, 4) + ' ' + value.slice(4);
      } else if (value.length > 6 && value.length <= 8) {
        value = value.slice(0, 4) + ' ' + value.slice(4, 7) + ' ' + value.slice(7);
      } else if (value.length > 8) {
        value = value.slice(0, 4) + ' ' + value.slice(4, 7) + ' ' + value.slice(7, 9) + ' ' + value.slice(9);
      }
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImages(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const togglePartStatus = (key) => {
    const states = ['original', 'local_painted', 'painted', 'changed'];
    const currentStatus = expertise[key];
    const nextIndex = (states.indexOf(currentStatus) + 1) % states.length;
    setExpertise(prev => ({ ...prev, [key]: states[nextIndex] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!kvkkApproved) {
      Swal.fire({
        title: 'Uyarı!',
        text: 'Devam etmek için lütfen Araç Satınalım KVKK Aydınlatma Metnini okuyup onaylayınız.',
        icon: 'warning',
        confirmButtonText: 'Tamam',
        background: '#0a0a0a',
        color: '#fff',
        confirmButtonColor: '#00d1ff'
      });
      return;
    }

    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/sell-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, images, expertise_report: expertise }),
      });

      const data = await response.json();
      if (response.ok) {
        Swal.fire({
          title: 'Başvurunuz Alındı!',
          text: data.message,
          icon: 'success',
          confirmButtonText: 'Tamam',
          background: '#0a0a0a',
          color: '#fff',
          confirmButtonColor: '#00d1ff'
        });
        setFormData({
          name: '', surname: '', phone: '', email: '',
          brand: '', model: '', year: '', mileage: '',
          fuel_type: 'Benzin', transmission: 'Otomatik',
          expected_price: '', notes: ''
        });
        setExpertise(ALL_PARTS.reduce((acc, part) => ({ ...acc, [part.key]: 'original' }), {}));
        setImages([]);
        setCurrentStep(1);
      } else {
        throw new Error(data.error || data.message || 'Bilinmeyen sunucu hatası');
      }
    } catch (error) {
      Swal.fire({ title: 'Hata!', text: error.message || 'Hata oluştu.', icon: 'error', background: '#0a0a0a', color: '#fff' });
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="bg-bg-main min-h-screen text-white">
      <Navbar />

      <main className="pt-64 lg:pt-[320px] pb-20">
        <div className="container px-6 lg:px-20">
          
          {/* Multi-layered Hero Section */}
          <div className="relative mb-24">
             <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary/10 rounded-full blur-[120px] pointer-events-none"></div>
             <div className="absolute top-0 -right-24 w-80 h-80 bg-blue-500/5 rounded-full blur-[100px] pointer-events-none"></div>
             
             <div className="relative z-10 flex flex-col lg:flex-row justify-between items-start gap-12" style={{marginTop: '150px'}}>
                <div className="max-w-3xl text-left">
                   <div className="inline-flex items-center gap-4 px-5 py-2.5 bg-white/5 rounded-full border border-white/10 mb-8 backdrop-blur-md">
                      <Zap size={14} className="text-primary animate-pulse" />
                      <span className="text-[10px] font-black uppercase tracking-widest text-white/90">PROFESYONEL EKSPERTİZ SİSTEMİ</span>
                   </div>
                   <h1 className="text-6xl lg:text-8xl font-black mb-12 tracking-tighter leading-tight text-left">
                      ARACINIZI<br />
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-blue-400 to-primary bg-[length:200%_auto] animate-gradient-x italic">NAKDE</span> ÇEVİRİN.
                   </h1>
                   <p className="text-text-muted-light text-lg lg:text-xl max-w-xl leading-relaxed font-medium mt-8 text-left" style={{marginBottom: '20px'}}>
                     Türkiye'nin en gelişmiş dijital araç alım platformu. Şeffaf süreç, adil fiyatlandırma ve <span className="text-white border-b-2 border-primary/30">aynı gün içerisinde</span> ödeme garantisi.
                   </p>
                </div>
                
                <div className="hidden lg:flex items-center gap-12 pb-4">
                   <StatBox value="15" label="DAKİKA" subLabel="Hızlı Analiz" />
                   <div className="w-[1px] h-16 bg-gradient-to-b from-transparent via-white/10 to-transparent"></div>
                   <StatBox value="0" label="SÜRPRİZ" subLabel="Şeffaf Ekspertiz" />
                   <div className="w-[1px] h-16 bg-gradient-to-b from-transparent via-white/10 to-transparent"></div>
                   <StatBox value="24" label="SAAT" subLabel="Banka Transferi" />
                </div>
             </div>

             {/* Animated Progress Bar */}
             <div className="mt-48 mb-24 max-w-4xl mx-auto px-4">
                <style>{`
                  .step-arrow-container {
                    display: flex;
                    flex: 1;
                    align-items: center;
                    justify-content: center;
                    padding: 0 10px;
                  }
                  .step-arrow {
                    width: 100%;
                    height: 1px;
                    background: rgba(255,255,255,0.05);
                    position: relative;
                  }
                  .step-arrow-active {
                    position: absolute;
                    top: 50%;
                    left: 0;
                    transform: translateY(-50%);
                    color: #00d1ff;
                    animation: slideArrow 2s infinite;
                    opacity: 0;
                  }
                  @keyframes slideArrow {
                    0% { left: 0%; opacity: 0; }
                    50% { opacity: 1; }
                    100% { left: 80%; opacity: 0; }
                  }
                `}</style>

                <div className="relative flex items-center justify-between">
                   {[
                     { id: 1, label: 'KİMLİK', icon: User },
                     { id: 2, label: 'ARAÇ', icon: Car },
                     { id: 3, label: 'DURUM', icon: ShieldCheck },
                     { id: 4, label: 'FİNAL', icon: Send }
                   ].map((step, idx) => (
                     <React.Fragment key={step.id}>
                        <div className="flex flex-col items-center gap-4 relative z-10 group cursor-pointer" onClick={() => currentStep > step.id && setCurrentStep(step.id)}>
                           <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-700 ${
                             currentStep === step.id ? 'bg-[#00d1ff] text-black shadow-[0_0_20px_rgba(0,209,255,0.3)]' : 
                             currentStep > step.id ? 'bg-green-500 text-white' : 
                             'bg-white/5 border border-white/10 text-gray-400'
                           }`}>
                              {currentStep > step.id ? <CheckCircle2 size={20} /> : <step.icon size={20} />}
                           </div>
                           <span className={`text-[10px] font-bold tracking-widest transition-colors duration-500 ${currentStep === step.id ? 'text-[#00d1ff]' : 'text-gray-500'}`}>{step.label}</span>
                        </div>
                        
                        {idx < 3 && (
                          <div className="step-arrow-container">
                             <div className="step-arrow">
                                <div className={`step-arrow-active ${currentStep > step.id ? 'block' : 'hidden'}`}>
                                   <ChevronRight size={16} />
                                </div>
                             </div>
                          </div>
                        )}
                     </React.Fragment>
                   ))}
                </div>
             </div>
          </div>

          <div className="max-w-3xl mx-auto px-4 lg:px-0 mb-24 "style={{marginTop: '150px'}}>
             <div className="contact-card-container " style={{ opacity: 1, transform: 'none' }}>
                <form className="contact-form" onSubmit={handleSubmit}>
                  
                  {/* Step 1: User Info */}
                  {currentStep === 1 && (
                    <div className="animate-in fade-in slide-in-from-bottom-12 duration-700">
                       <div className="contact-form-header">
                          <h3 className="contact-form-title">Size Nasıl Ulaşalım?</h3>
                          <p className="contact-form-desc">Formu doldurarak bizimle iletişime geçebilirsiniz. En kısa sürede size profesyonel bir teklif hazırlayacağız.</p>
                       </div>
                       
                       <div className="contact-form-row gap-y-10 pt-8">
                          <ContactInput label="Ad *" name="name" value={formData.name} onChange={handleInputChange} required />
                          <ContactInput label="Soyad *" name="surname" value={formData.surname} onChange={handleInputChange} required />
                       </div>
                       <div className="contact-form-row gap-y-10 mt-12">
                          <ContactInput label="Telefon Numarası *" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} required placeholder="0XXX XXX XX XX" />
                          <ContactInput label="E-Posta Adresi *" name="email" type="email" value={formData.email} onChange={handleInputChange} required />
                       </div>
                    </div>
                  )}

                  {/* Step 2: Vehicle Selection */}
                  {currentStep === 2 && (
                    <div className="animate-in fade-in slide-in-from-right-12 duration-700">
                       <div className="contact-form-header">
                          <h3 className="contact-form-title">Araç Detayları</h3>
                          <p className="contact-form-desc">Aracınızın teknik özelliklerini girerek ekspertiz sürecini anında başlatabilirsiniz.</p>
                       </div>
                       
                       <div className="contact-form-row gap-y-10 pt-8">
                          <ContactSelect 
                             label="Marka Seçimi *" 
                             name="brand" 
                             value={formData.brand} 
                             onChange={handleInputChange} 
                             defaultText="Lütfen Marka Seçiniz"
                             options={brands.map(b => ({ value: b.Make_Name, label: b.Make_Name }))}
                          />
                          <ContactSelect 
                             label="Model Seçimi *" 
                             name="model" 
                             value={formData.model} 
                             onChange={handleInputChange} 
                             disabled={!formData.brand}
                             defaultText={!formData.brand ? 'Önce Marka Seçin' : 'Model Seçiniz'}
                             options={models.map(m => ({ value: m.Model_Name, label: m.Model_Name }))}
                          />
                       </div>

                       <div className="contact-form-row gap-y-10 mt-12">
                          <ContactInput label="Model Yılı *" name="year" value={formData.year} onChange={handleInputChange} placeholder="202X" required />
                          <ContactInput label="Kilometre *" name="mileage" value={formData.mileage} onChange={handleInputChange} placeholder="35.000" required />
                       </div>

                       <div className="contact-form-row gap-y-10 mt-12">
                          <ContactSelect 
                             label="Yakıt Tipi *" 
                             name="fuel_type" 
                             value={formData.fuel_type} 
                             onChange={handleInputChange} 
                             options={[{value:'Benzin', label:'Benzin'}, {value:'Dizel', label:'Dizel'}, {value:'Hibrit', label:'Hibrit'}, {value:'Elektrik', label:'Elektrik'}]}
                          />
                          <ContactSelect 
                             label="Vites Tipi *" 
                             name="transmission" 
                             value={formData.transmission} 
                             onChange={handleInputChange} 
                             options={[{value:'Otomatik', label:'Otomatik'}, {value:'Manuel', label:'Manuel'}]}
                          />
                       </div>
                    </div>
                  )}

                  {/* Step 3: Interactive Expertise */}
                  {currentStep === 3 && (
                    <div className="animate-in fade-in slide-in-from-right-12 duration-700">
                       <div className="contact-form-header">
                          <h3 className="contact-form-title">Hasar Durumu</h3>
                          <p className="contact-form-desc">Aşağıdaki araç şeması üzerinden parçalara tıklayarak aracınızın güncel boya ve değişen durumunu işaretleyebilirsiniz.</p>
                       </div>
                       <div className="flex flex-col lg:flex-row justify-between items-start gap-6 mb-8">
                          <div className="flex flex-wrap gap-4 px-6 py-4 bg-white/5 rounded-3xl border border-white/10">
                             {[{l:'ORİJİNAL',c:COLOR_MAP.original},{l:'LOKAL',c:COLOR_MAP.local_painted},{l:'BOYALI',c:COLOR_MAP.painted},{l:'DEĞİŞEN',c:COLOR_MAP.changed}].map(s=>(
                               <div key={s.l} className="flex items-center gap-3">
                                  <div className="w-3 h-3 rounded-full" style={{ background: s.c }}></div>
                                  <span className="text-[9px] font-black text-gray-400 tracking-widest">{s.l}</span>
                               </div>
                             ))}
                          </div>
                       </div>

                       <div className="grid xl:grid-cols-2 gap-16 items-center">
                          <InteractiveCarMap expertise={expertise} togglePartStatus={togglePartStatus} />
                          <div className="space-y-4 max-h-[400px] overflow-y-auto pr-4 custom-scrollbar">
                             {ALL_PARTS.map(part => {
                               const status = expertise[part.key];
                               const getStatusLabel = (val) => {
                                 if (val === 'original') return 'ORİJİNAL';
                                 if (val === 'local_painted') return 'LOKAL BOYA';
                                 if (val === 'painted') return 'BOYALI';
                                 if (val === 'changed') return 'DEĞİŞEN';
                                 return val;
                               };
                               return (
                               <button 
                                 key={part.key} 
                                 type="button" 
                                 onClick={() => togglePartStatus(part.key)}
                                 className="w-full flex items-center justify-between p-4 rounded-2xl bg-white/5 border border-white/5 hover:border-primary/40 transition-all group"
                               >
                                  <span className="text-sm font-bold text-white tracking-wide">{part.label}</span>
                                  <span className="text-[10px] font-black px-4 py-1.5 rounded-lg uppercase tracking-widest shadow-lg" style={{ background: COLOR_MAP[status], color: status === 'original' ? 'rgba(255,255,255,0.4)' : 'white' }}>
                                     {getStatusLabel(status)}
                                  </span>
                               </button>
                               );
                             })}
                          </div>
                       </div>
                    </div>
                  )}

                  {/* Step 4: Images & Notes */}
                  {currentStep === 4 && (
                    <div className="animate-in fade-in slide-in-from-right-12 duration-700">
                       <div className="contact-form-header">
                          <h3 className="contact-form-title">Son Dokunuşlar</h3>
                          <p className="contact-form-desc">Aracınızın fotoğraflarını yükleyin ve fiyat beklentinizi belirtin.</p>
                       </div>
                       
                       <div className="space-y-12 pt-8">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                             {images.map((img, idx) => (
                                <div 
                                  key={idx} 
                                  draggable
                                  onDragStart={() => setDraggedItemIndex(idx)}
                                  onDragOver={(e) => e.preventDefault()}
                                  onDrop={(e) => {
                                    e.preventDefault();
                                    if (draggedItemIndex === null || draggedItemIndex === idx) return;
                                    const newImages = [...images];
                                    const item = newImages.splice(draggedItemIndex, 1)[0];
                                    newImages.splice(idx, 0, item);
                                    setImages(newImages);
                                    setDraggedItemIndex(null);
                                  }}
                                  onDragEnd={() => setDraggedItemIndex(null)}
                                  className={`relative aspect-square rounded-[2rem] overflow-hidden group border cursor-move transition-transform ${draggedItemIndex === idx ? 'opacity-50 scale-95 border-primary border-2 shadow-2xl' : 'border-white/10 hover:border-white/30'}`}
                                >
                                   <div className="absolute top-3 left-3 bg-black/50 px-3 py-1 rounded-full text-[10px] font-bold z-10 backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity">
                                     Taşı
                                   </div>
                                   <img src={img} alt="car" className="w-full h-full object-cover pointer-events-none" />
                                   <button type="button" onClick={() => removeImage(idx)} className="absolute top-3 right-3 bg-red-500/90 p-2.5 rounded-xl opacity-0 group-hover:opacity-100 transition-all hover:bg-red-600 shadow-xl cursor-default z-20">
                                      <Trash2 size={16} />
                                   </button>
                                </div>
                             ))}
                             {images.length < 12 && (
                               <label className="aspect-square rounded-[2rem] border-2 border-dashed border-white/10 flex flex-col items-center justify-center cursor-pointer hover:border-primary/40 hover:bg-white/5 transition-all group overflow-hidden bg-white/[0.01]">
                                  <input type="file" multiple onChange={handleImageUpload} className="hidden" accept="image/*" />
                                  <UploadCloud size={30} className="text-gray-600 group-hover:text-primary transition-colors duration-500" />
                                  <span className="text-[9px] font-black text-gray-500 uppercase mt-4 tracking-widest">FOTOĞRAF EKLE</span>
                               </label>
                             )}
                          </div>

                          <div className="contact-form-row gap-y-10 mt-12">
                             <ContactInput label="Fiyat Beklentiniz (₺)" name="expected_price" value={formData.expected_price} onChange={handleInputChange} placeholder="Örn: 1.850.000" />
                          </div>
                          <div className="mt-12">
                             <label className="contact-field-label" style={{ color: 'var(--primary)', transform: 'translateY(-12px) scale(0.8)', display:'block', marginBottom:'8px' }}>Ek Notlar & Opsiyonlar</label>
                             <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows="4" className="contact-field-input contact-textarea" placeholder="Aracın artıları, ekstraları ve boyalı parçaların nedenlerini buraya yazabilirsiniz..." />
                          </div>
                       </div>
                    </div>
                  )}

                  <div className="mt-14 pt-10 flex flex-col items-center justify-center relative w-full gap-4">
                     {currentStep > 1 && (
                       <div className="w-full md:absolute md:left-0 md:top-1/2 md:-translate-y-1/2 md:w-auto">
                           <button type="button" onClick={() => setCurrentStep(currentStep - 1)} className="w-full bg-white/10 text-white hover:bg-white/20 border border-white/10 shadow-lg px-8 py-4 rounded-xl font-bold text-sm tracking-wide transition-all flex items-center justify-center gap-2">
                              <ChevronLeft size={18} /> GERİ
                           </button>
                       </div>
                     )}
                     <div className="w-full md:w-[320px]">
                        <button type="submit" disabled={loading} className="contact-submit-btn m-0 w-full">
                           {currentStep < 4 ? <ChevronRight size={18} /> : <Send size={18} />}
                           <span>{loading ? 'İşleniyor...' : currentStep === 4 ? 'Talebi Gönder' : 'Sıradaki Adım'}</span>
                        </button>
                     </div>
                     <div className="contact-checkbox-wrapper mt-6 w-full justify-center">
                        <input 
                           type="checkbox" 
                           id="aydinlatma" 
                           className="contact-checkbox" 
                           checked={kvkkApproved}
                           onChange={(e) => setKvkkApproved(e.target.checked)}
                        />
                        <label htmlFor="aydinlatma" className="contact-checkbox-label">
                           <Link to="/kvkk" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>KVKK Aydınlatma Metni</Link>'ni okudum ve onaylıyorum. *
                        </label>
                     </div>
                  </div>

               </form>
             </div>
          </div>

          {/* Bottom Sections */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20  max-w-5xl mx-auto px-4 lg:px-0" style={{marginTop:"150px"}}>
             <div className="bg-[#0b0c0f] rounded-[3.5rem] p-16 border border-white/5 relative">
                <h4 className="text-2xl font-black mb-12 flex items-center gap-6">
                   <span className="w-1.5 h-8 bg-primary rounded-full"></span> SÜREÇ NASIL İŞLER?
                </h4>
                <div className="grid sm:grid-cols-2 gap-10">
                   <ProcessStep number="01" title="Başvurunuzu Yapın" desc="Formu doldurup aracınızın fotoğraflarını yükleyin." />
                   <ProcessStep number="02" title="Anında Değerleme" desc="Algoritmalarımız ve uzmanlarımız piyasayı analiz etsin." />
                   <ProcessStep number="03" title="Teklif & Randevu" desc="Size en adil teklifi sunalım ve fiziki kontrole çağıralım." />
                   <ProcessStep number="04" title="Nakit Ödeme" desc="Satış günü noter işlemlerinden sonra ödemeniz bankada." />
                </div>
             </div>

             <div className="p-[2px] rounded-[3.5rem] bg-gradient-to-br from-primary via-blue-500 to-purple-600 shadow-2xl">
                <div className="bg-[#080808] rounded-[3.4rem] p-16 text-center flex flex-col items-center h-full justify-center">
                   <div className="w-24 h-24 rounded-[2.5rem] bg-primary/10 flex items-center justify-center mb-10 mt-8 rotate-6 border border-primary/20">
                      <Phone size={44} className="text-primary" />
                   </div>
                   <h4 className="text-3xl font-black mb-6 tracking-tighter">PROFESYONEL DESTEK</h4>
                   <p className="text-sm font-bold text-gray-500 mb-12 tracking-widest leading-relaxed max-w-xs">Sorularınız için uzman ekibimize 7/24 ulaşabilirsiniz.</p>
                   <a href="tel:+905321585646" className="text-4xl font-black text-white hover:text-primary transition-all tracking-tighter underline underline-offset-[12px] decoration-primary/30">+90 532 158 56 46</a>
                </div>
             </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

const InteractiveCarMap = ({ expertise, togglePartStatus }) => (
  <div className="flex justify-center py-10 scale-125 lg:scale-150">
    <div style={{ width: '100%', maxWidth: '280px' }}>
      <svg viewBox="0 0 500 650" style={{ width: '100%', height: 'auto' }}>
        <g transform="translate(250, 20)">
          {/* Main vertical parts */}
          <ClickablePart partKey="front_bumper" d="M -50,15 L 50,15 L 50,30 Q 50,35 45,35 L -45,35 Q -50,35 -50,30 Z" expertise={expertise} toggle={togglePartStatus} />
          <ClickablePart partKey="hood" d="M -55,45 Q 0,35 55,45 L 70,165 Q 0,180 -70,165 Z" expertise={expertise} toggle={togglePartStatus} />
          <ClickablePart partKey="roof" d="M -85,235 Q 0,250 85,235 L 90,420 Q 0,440 -90,420 Z" expertise={expertise} toggle={togglePartStatus} />
          <ClickablePart partKey="trunk" d="M -80,480 Q 0,500 80,480 L 70,590 Q 0,610 -70,590 Z" expertise={expertise} toggle={togglePartStatus} />
          <ClickablePart partKey="rear_bumper" d="M -55,600 L 55,600 L 55,620 Q 55,630 45,630 L -45,630 Q -55,630 -55,620 Z" expertise={expertise} toggle={togglePartStatus} />
          
          {/* Side parts */}
          <ClickablePart partKey="front_left_fender" d="M -100,55 L -160,70 Q -185,80 -185,120 L -185,170 L -110,170 Z" expertise={expertise} toggle={togglePartStatus} />
          <ClickablePart partKey="front_left_door" d="M -115,185 L -200,230 L -210,330 L -120,320 Z" expertise={expertise} toggle={togglePartStatus} />
          <ClickablePart partKey="rear_left_door" d="M -130,340 L -215,360 L -220,480 L -135,480 Z" expertise={expertise} toggle={togglePartStatus} />
          <ClickablePart partKey="rear_left_fender" d="M -135,495 L -225,495 Q -235,560 -210,590 L -120,610 Z" expertise={expertise} toggle={togglePartStatus} />
          
          <ClickablePart partKey="front_right_fender" d="M 100,55 L 160,70 Q 185,80 185,120 L 185,170 L 110,170 Z" expertise={expertise} toggle={togglePartStatus} />
          <ClickablePart partKey="front_right_door" d="M 115,185 L 200,230 L 210,330 L 120,320 Z" expertise={expertise} toggle={togglePartStatus} />
          <ClickablePart partKey="rear_right_door" d="M 130,340 L 215,360 L 220,480 L 135,480 Z" expertise={expertise} toggle={togglePartStatus} />
          <ClickablePart partKey="rear_right_fender" d="M 135,495 L 225,495 Q 235,560 210,590 L 120,610 Z" expertise={expertise} toggle={togglePartStatus} />
        </g>
      </svg>
    </div>
  </div>
);

const ClickablePart = ({ partKey, d, expertise, toggle }) => {
  const status = expertise[partKey];
  return (
    <path 
      d={d} 
      fill={COLOR_MAP[status]} 
      stroke="rgba(255,255,255,0.15)" 
      strokeWidth="2"
      onClick={() => toggle(partKey)}
      className="cursor-pointer transition-all duration-300 hover:brightness-125 active:scale-95"
      style={{ filter: status !== 'original' ? 'drop-shadow(0 0 5px rgba(0,0,0,0.5))' : 'none' }}
    />
  );
};

const StatBox = ({ value, label, subLabel }) => (
  <div className="text-left flex flex-col gap-1 group cursor-default">
     <div className="flex items-baseline gap-2">
        <span className="text-5xl font-black text-white group-hover:text-primary transition-colors duration-500">{value}</span>
        <span className="text-sm font-black text-primary tracking-widest">{label}</span>
     </div>
     <span className="text-[10px] font-bold text-gray-600 uppercase tracking-[0.2em]">{subLabel}</span>
  </div>
);


const ProcessStep = ({ number, title, desc }) => (
  <div className="flex items-start gap-8 group">
    <div className="text-4xl font-black text-white/5 group-hover:text-primary/20 transition-colors duration-500">{number}</div>
    <div>
       <h5 className="text-[11px] font-black text-white uppercase tracking-widest mb-2">{title}</h5>
       <p className="text-[10px] font-bold text-gray-600 leading-relaxed">{desc}</p>
    </div>
  </div>
);

export default SellCar;
